import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common";
import { RosterWizard } from "@/components/roster/RosterWizard";
import { RosterHelp } from "@/components/roster/RosterHelp";
import { Link } from "react-router-dom";
import { RosterList } from "@/components/roster/RosterList";
import { RosterHomeHeader } from "@/components/roster/RosterHome";
import { FinalRoster } from "@/components/roster/FinalRoster";
import { RosterUpdates } from "@/components/roster/RosterUpdates";
import { CopyShiftsDialog, ScopePicks, useShiftMasters, placedToast } from "@/components/roster/CopyShiftsDialog";
import { ShiftImport } from "@/components/roster/ShiftImport";
import { ShiftRequestPanel, ShiftRequestApprovals } from "@/components/roster/ShiftRequests";
import { Checkbox } from "@/components/ui/checkbox";
import { TimePicker } from "@/components/pickers";
import { formatTime12, shiftInfo } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Trash2, Send, Copy, ChevronLeft, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const ALL = "__all__";

export default function Roster() {
  const { isManagerOnly } = useAuth();
  const [plan, setPlan] = useState(null);
  const [creating, setCreating] = useState(false);
  const open = (p) => { setCreating(false); setPlan(p); };
  const home = (
    <>
      <RosterHomeHeader onCreate={() => setCreating(true)} />
      {isManagerOnly && <RosterUpdates />}
      {!isManagerOnly && <PendingApprovals onOpen={open} />}
      <RosterList onOpen={open} />
      {!isManagerOnly && <FinalRoster />}
    </>
  );
  const create = (
    <>
      <Button variant="outline" className="mb-4" onClick={() => setCreating(false)} data-testid="back-to-rosters-btn"><ChevronLeft className="mr-1 h-4 w-4" /> All rosters</Button>
      <RosterHelp />
      <RosterStart onOpen={open} />
    </>
  );
  const body = plan ? <RosterWizard key={plan.id} plan={plan} onExit={() => setPlan(null)} /> : creating ? create : home;
  return (
    <div>
      <PageHeader title="Roster" description={isManagerOnly ? "Build rosters for your team and send them to your org admin for approval." : "Build, approve and export monthly shift rosters."} />
      <Tabs defaultValue="rosters">
        <TabsList className="mb-5">
          <TabsTrigger value="rosters" data-testid="tab-plan">Rosters</TabsTrigger>
          {isManagerOnly
            ? <TabsTrigger value="requests" data-testid="tab-shift-requests">Shift requests</TabsTrigger>
            : <TabsTrigger value="shifts" data-testid="tab-shifts">Shifts & rules</TabsTrigger>}
        </TabsList>
        <TabsContent value="rosters">{body}</TabsContent>
        {isManagerOnly
          ? <TabsContent value="requests"><ShiftRequestPanel /></TabsContent>
          : <TabsContent value="shifts"><ShiftsTab /></TabsContent>}
      </Tabs>
    </div>
  );
}

function PendingApprovals({ onOpen }) {
  const { actingOrgId } = useAuth();
  const qc = useQueryClient();
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const { data = [] } = useQuery({ queryKey: ["roster-approvals", actingOrgId], queryFn: async () => (await api.get("/roster/approvals")).data, staleTime: 0, refetchOnMount: "always" });
  if (!data.length) return null;
  const ids = data.map((p) => p.id);
  const sel = picked.filter((id) => ids.includes(id));
  const all = sel.length === ids.length;
  const approve = async () => {
    if (!window.confirm(`Approve and publish ${sel.length} roster(s)?`)) return;
    setBusy(true);
    try {
      const { data: r } = await api.post("/roster/approvals/approve", { ids: sel });
      if (r.approved.length) toast.success(`${r.approved.length} roster(s) approved and published`);
      if (r.failed.length) toast.error(`${r.failed.length} could not be approved: ${r.failed[0].reason}`);
      setPicked([]);
      qc.invalidateQueries({ queryKey: ["roster-approvals"] });
      qc.invalidateQueries({ queryKey: ["roster-visible"] });
      qc.invalidateQueries({ queryKey: ["final-status"] });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Card className="mb-5 border-2 border-amber-300 bg-amber-50 p-5" data-testid="pending-approvals">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-2 text-base font-bold text-amber-900"><Send className="h-5 w-5" /> {data.length} roster(s) waiting for your approval</p>
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-sm font-semibold text-amber-900"><Checkbox checked={all} onCheckedChange={(v) => setPicked(v ? ids : [])} data-testid="approval-select-all" /> Select all</label>
          <Button size="sm" disabled={!sel.length || busy} onClick={approve} data-testid="approval-bulk-approve"><CheckCircle2 className="mr-1 h-4 w-4" /> {busy ? "Approving…" : `Approve & publish (${sel.length})`}</Button>
        </div>
      </div>
      <div className="space-y-2">
        {(showAll ? data : data.slice(0, 5)).map((p) => (
          <div key={p.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-white px-4 py-3" data-testid={`approval-${p.id}`}>
            <label className="flex min-w-0 flex-1 items-center gap-3">
            <Checkbox checked={sel.includes(p.id)} onCheckedChange={(v) => setPicked(v ? [...sel, p.id] : sel.filter((x) => x !== p.id))} data-testid={`approval-select-${p.id}`} />
            <span className="text-base font-semibold text-slate-900">{p.location_name}{p.department_name ? ` · ${p.department_name}` : ""} — {MONTHS[p.month - 1]} {p.year}
              <span className="ml-2 break-all text-sm font-normal text-slate-600">by {p.submitted_by}</span>
              {p.change_request?.status === "pending" && <span className="ml-2 whitespace-nowrap rounded-full bg-amber-200 px-2 py-0.5 text-xs font-semibold text-amber-900" data-testid={`approval-cr-${p.id}`}>Change requested</span>}</span>
            </label>
            <Button size="sm" onClick={() => onOpen(p)} data-testid={`approval-review-${p.id}`}>Review</Button>
          </div>
        ))}
      </div>
      {data.length > 5 && <Button size="sm" variant="ghost" className="mt-2 text-amber-900" onClick={() => setShowAll(!showAll)} data-testid="approvals-toggle-all">{showAll ? "Show fewer" : `Show all ${data.length}`}</Button>}
    </Card>
  );
}

function MultiPick({ label, items, value, onChange, testid }) {
  const toggle = (id) => onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  return (
    <div className="min-w-[220px] flex-1">
      <p className="mb-1.5 text-sm font-semibold text-slate-800">{label} <span className="font-normal text-slate-500">({value.length ? `${value.length} selected` : "all"})</span></p>
      <div className="flex max-h-40 flex-wrap gap-x-4 gap-y-2 overflow-y-auto rounded-md border border-slate-200 p-3" data-testid={testid}>
        {items.map((x) => <label key={x.id} className="flex items-center gap-2 text-sm"><Checkbox checked={value.includes(x.id)} onCheckedChange={() => toggle(x.id)} data-testid={`${testid}-${x.id}`} /> {x.name}</label>)}
      </div>
    </div>
  );
}

function useScopeLimit() {
  const { user, isManagerOnly } = useAuth();
  if (!isManagerOnly) return () => true;
  const scope = (user?.memberships || []).filter((m) => m.role === "center_manager").flatMap((m) => m.scope || []);
  return (k, id) => scope.some((s) => !s[k] || s[k] === id);
}

function RosterStart({ onOpen }) {
  const { actingOrgId, isManagerOnly } = useAuth();
  const allowed = useScopeLimit();
  const now = new Date();
  const [f, setF] = useState({ location_id: "", department_id: "", month: String(now.getMonth() + 1), year: String(now.getFullYear()) });
  const [mode, setMode] = useState("single");
  const multi = mode !== "single";
  const [ml, setMl] = useState({ location_ids: [], department_ids: [] });
  const { data: allLocs = [] } = useQuery({ queryKey: ["r-locs", actingOrgId], queryFn: async () => (await api.get("/org/masters/locations")).data });
  const { data: allDepts = [] } = useQuery({ queryKey: ["r-depts", actingOrgId], queryFn: async () => (await api.get("/org/masters/departments")).data });
  const locs = allLocs.filter((l) => allowed("location_id", l.id));
  const depts = allDepts.filter((d) => allowed("department_id", d.id));
  useEffect(() => { if (locs.length && !f.location_id) setF((p) => ({ ...p, location_id: locs[0].id })); }, [locs, f.location_id]);
  const open = async () => {
    try {
      const scope = mode === "pick" ? { location_ids: [], department_ids: [], pick: true } : mode === "dept" ? { location_ids: [], department_ids: ml.department_ids } : ml;
      const body = multi ? { multi: true, ...scope, year: +f.year, month: +f.month } : { ...f, year: +f.year, month: +f.month };
      const { data } = await api.post("/roster/plans/open", body); onOpen(data);
    }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const years = [now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map(String);
  const field = (label, k, items, testid, allLabel) => (
    <div className="min-w-[200px] flex-1"><p className="mb-1.5 text-sm font-semibold text-slate-800">{label}</p>
      <Select value={f[k] || ALL} onValueChange={(v) => setF({ ...f, [k]: v === ALL ? "" : v })}>
        <SelectTrigger className="h-11 text-base" data-testid={testid}><SelectValue placeholder="Select" /></SelectTrigger>
        <SelectContent>{allLabel && <SelectItem value={ALL}>{allLabel}</SelectItem>}{items.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
      </Select></div>
  );
  return (
    <Card className="p-6">
      <p className="text-xl font-bold text-slate-900">Start or open a roster</p>
      <p className="mb-4 text-sm text-slate-600">Pick where and when. If a roster already exists for this choice, it opens where you left off.</p>
      <div className="mb-5 inline-flex flex-wrap rounded-lg bg-slate-100 p-1" data-testid="roster-scope-toggle">
        {[["single", "One location"], ["dept", "Department across locations"], ["multi", "Several locations & departments"], ["pick", "Pick employees by name"]].map(([v, l]) => (
          <button key={v} onClick={() => setMode(v)} data-testid={`scope-${v}`}
            className={`rounded-md px-4 py-2 text-sm font-semibold ${mode === v ? "bg-white text-indigo-700 shadow-sm" : "text-slate-600"}`}>{l}</button>
        ))}
      </div>
      <div className="flex flex-wrap items-end gap-4">
        {mode === "multi" && <>
          <MultiPick label="Locations" items={locs} value={ml.location_ids} onChange={(v) => setMl({ ...ml, location_ids: v })} testid="multi-loc" />
          <MultiPick label="Departments" items={depts} value={ml.department_ids} onChange={(v) => setMl({ ...ml, department_ids: v })} testid="multi-dept" />
        </>}
        {mode === "dept" && <MultiPick label="Departments" items={depts} value={ml.department_ids} onChange={(v) => setMl({ ...ml, department_ids: v })} testid="dept-scope-dept" />}
        {mode === "single" && <>
          {field("Location", "location_id", locs, "roster-location")}
          {field("Department (optional)", "department_id", depts, "roster-department", "All departments")}
        </>}
        {field("Month", "month", MONTHS.map((m, i) => ({ id: String(i + 1), name: m })), "plan-month")}
        {field("Year", "year", years.map((y) => ({ id: y, name: y })), "plan-year")}
        <Button size="lg" className="h-11" disabled={(mode === "single" && !f.location_id) || (mode === "dept" && !ml.department_ids.length)} onClick={open} data-testid="open-roster-btn">Continue</Button>
      </div>
      {mode === "multi" && <p className="mt-3 text-sm text-slate-600">Nothing ticked means all{isManagerOnly ? " within your access" : ""}. The roster uses shared shifts plus the shifts of the chosen locations, and the rules of the first location.</p>}
      {mode === "dept" && <p className="mt-3 text-sm text-slate-600">Everyone in the ticked department(s) across all locations{isManagerOnly ? " within your access" : ""}.</p>}
      {mode === "pick" && <p className="mt-3 text-sm text-slate-600">Next, search by name or filter by location, department or designation and tick the people for this roster. Everything else works the same.</p>}
      {!locs.length && <p className="mt-4 text-sm text-amber-700">No locations yet. An org admin adds locations under Masters.</p>}
    </Card>
  );
}

function ShiftsTab() {
  const { actingOrgId } = useAuth();
  const [locId, setLocId] = useState("");
  const [rev, setRev] = useState(0);
  const { data: locs = [] } = useQuery({ queryKey: ["r-locs", actingOrgId], queryFn: async () => (await api.get("/org/masters/locations")).data });
  useEffect(() => { if (locs.length && !locId) setLocId(locs[0].id); }, [locs, locId]);
  return (
    <div className="space-y-4">
      <ShiftRequestApprovals onApproved={() => setRev((r) => r + 1)} />
      <RosterHelp />
      <ShiftImport onDone={() => setRev((r) => r + 1)} />
      <Card className="flex items-center gap-3 p-4">
        <span className="text-sm font-semibold text-slate-800">Location</span>
        <Select value={locId} onValueChange={setLocId}>
          <SelectTrigger className="w-64" data-testid="shifts-location"><SelectValue placeholder="Select location" /></SelectTrigger>
          <SelectContent><SelectItem value="__library__" data-testid="shifts-location-library">Shift library (not yet assigned)</SelectItem><SelectItem value="__all__" data-testid="shifts-location-all">All locations (shared shifts)</SelectItem>{locs.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
        </Select>
      </Card>
      {locId && <ShiftsPanel key={`${locId}-${rev}`} locId={locId} />}
    </div>
  );
}

function ShiftsPanel({ locId }) {
  const [shifts, setShifts] = useState([]);
  const [form, setForm] = useState({ code: "", name: "", start: "", end: "" });
  const { locs, depts } = useShiftMasters();
  const library = locId === "__library__";
  const [scope, setScope] = useState({ location_ids: library || locId === "__all__" ? [] : [locId], department_ids: [] });
  const [rules, setRules] = useState(null);
  const [picked, setPicked] = useState([]);
  const [copyOpen, setCopyOpen] = useState(false);
  const togglePick = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const shared = locId === "__all__";
  const load = async () => {
    setShifts((await api.get(`/roster/shifts?location_id=${locId}`)).data);
    setRules(shared || library ? null : (await api.get(`/roster/location-rules/${locId}`)).data);
  };
  useEffect(() => { load(); }, [locId]); // eslint-disable-line react-hooks/exhaustive-deps

  const add = async () => {
    try {
      if (shared) { await api.post("/roster/shifts", { ...form, location_id: locId }); toast.success("Shift added"); }
      else placedToast((await api.post("/roster/shifts/bulk-create", { ...form, ...scope, library })).data);
      setForm({ code: "", name: "", start: "", end: "" }); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/roster/shifts/${id}`); load(); };
  const saveRules = async () => {
    try { await api.put(`/roster/location-rules/${locId}`, rules); toast.success("Rules saved"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4">
      <Card className="p-5">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <p className="text-base font-bold text-slate-900">{shared ? "Shared shifts (all locations)" : library ? "Shift library" : "Shift definitions"}</p>
          {!shared && !!shifts.length && <Button size="sm" variant="ghost" onClick={() => setPicked(picked.length === shifts.length ? [] : shifts.map((s) => s.id))} data-testid="pick-all-shifts">{picked.length === shifts.length ? "Clear selection" : "Select all shifts"}</Button>}
          {!shared && <Button size="sm" variant="outline" className="h-auto min-h-8 max-w-full whitespace-normal py-1.5 text-left" disabled={!picked.length} onClick={() => setCopyOpen(true)} data-testid="copy-shifts-btn">
            <Copy className="mr-1 h-4 w-4" /> Assign {picked.length || ""} selected to locations / departments
          </Button>}
        </div>
        {library && <p className="mb-3 text-sm text-slate-600" data-testid="library-note">Shifts here are not used in any roster yet. Tick them and use Assign to place them in one, several or all locations and departments.</p>}
        {shared && <p className="mb-3 text-sm text-slate-600" data-testid="shared-shifts-note">These shifts show up in every location's rosters. Change one here and it changes everywhere. Use a code that no single location already uses.</p>}
        <CopyShiftsDialog open={copyOpen} onOpenChange={setCopyOpen} shiftIds={picked} onDone={() => { setCopyOpen(false); setPicked([]); load(); }} />
        <div className="mb-4 flex flex-wrap items-end gap-3">
          <div><p className="mb-1 text-sm text-slate-700">Code</p><Input placeholder="M" maxLength={4} value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className="w-20" data-testid="shift-code" /></div>
          <div><p className="mb-1 text-sm text-slate-700">Name</p><Input placeholder="Morning" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-40" data-testid="shift-name" /></div>
          <div><p className="mb-1 text-sm text-slate-700">Start</p><TimePicker value={form.start} onChange={(v) => setForm({ ...form, start: v })} testid="shift-start" /></div>
          <div><p className="mb-1 text-sm text-slate-700">End</p><TimePicker value={form.end} onChange={(v) => setForm({ ...form, end: v })} testid="shift-end" /></div>
          <Button onClick={add} disabled={!shared && !library && !scope.location_ids.length} data-testid="add-shift"><Plus className="mr-1 h-4 w-4" /> Add</Button>
          {shiftInfo(form.start, form.end) && (
            <span className={`self-center rounded-md px-3 py-1.5 text-sm font-semibold ${shiftInfo(form.start, form.end).overnight ? "bg-indigo-100 text-indigo-900" : "bg-slate-100 text-slate-800"}`} data-testid="shift-duration-hint">
              {shiftInfo(form.start, form.end).label}
            </span>
          )}
        </div>
        {!shared && !library && (
          <div className="mb-4 flex flex-wrap gap-4 rounded-lg bg-slate-50 p-3" data-testid="shift-create-scope">
            <ScopePicks label="Create in locations" hint="pick at least one" items={locs} value={scope.location_ids} onChange={(v) => setScope({ ...scope, location_ids: v })} testid="create-loc" />
            <ScopePicks label="For departments" hint="all departments" items={depts} value={scope.department_ids} onChange={(v) => setScope({ ...scope, department_ids: v })} testid="create-dept" />
          </div>
        )}
        <div className="space-y-2">
          {shifts.map((s) => (
            <div key={s.id} className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-2.5 text-base text-slate-900" data-testid={`shift-${s.code}`}>
              <span className="flex items-center gap-3">{!shared && <Checkbox checked={picked.includes(s.id)} onCheckedChange={() => togglePick(s.id)} data-testid={`pick-shift-${s.code}`} />}
                <span><b>{s.code}</b> · {s.name} · {formatTime12(s.start)} – {formatTime12(s.end)}
                <span className="ml-2 rounded bg-slate-100 px-2 py-0.5 text-sm font-semibold text-slate-700">{shiftInfo(s.start, s.end)?.label}</span>{s.is_night ? " · night" : ""}
                <span className="ml-2 text-sm text-slate-500" data-testid={`shift-depts-${s.code}`}>{s.departments?.length ? (s.departments.length === depts.length ? `All departments (${depts.length})` : s.departments.slice(0, 3).map((d) => depts.find((x) => x.id === d)?.name || "?").join(", ") + (s.departments.length > 3 ? ` +${s.departments.length - 3}` : "")) : "All departments"}</span></span></span>
              <button onClick={() => del(s.id)} data-testid={`delete-shift-${s.code}`}><Trash2 className="h-4 w-4 text-rose-500" /></button>
            </div>
          ))}
          {!shifts.length && <p className="text-sm text-slate-500">No shifts yet.</p>}
        </div>
      </Card>
      {rules && (
        <Card className="p-5">
          <p className="mb-3 text-base font-bold text-slate-900">Location rules</p>
          <div className="grid gap-4 md:grid-cols-3">
            <L label="Min rest hours"><Input type="number" value={rules.min_rest_hours} onChange={(e) => setRules({ ...rules, min_rest_hours: +e.target.value })} data-testid="min-rest" /></L>
            <L label="Max consecutive days"><Input type="number" value={rules.max_consec_days} onChange={(e) => setRules({ ...rules, max_consec_days: +e.target.value })} /></L>
            <L label="Max consecutive nights"><Input type="number" value={rules.max_consec_nights} onChange={(e) => setRules({ ...rules, max_consec_nights: +e.target.value })} /></L>
            <L label="Auto week offs (days per week)"><Input type="number" value={rules.weekly_off?.n ?? 1} onChange={(e) => setRules({ ...rules, weekly_off: { type: "rotational", n: +e.target.value } })} data-testid="weekly-off-n" /></L>
          </div>
          <p className="mt-6 rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="holidays-moved-note">Holidays are now managed for all locations and departments on the <Link to="/org/holidays" className="font-semibold underline">Holidays</Link> page.</p>
          <Button className="mt-4" onClick={saveRules} data-testid="save-rules">Save rules</Button>
        </Card>
      )}
    </div>
  );
}

const L = ({ label, children }) => <div><label className="text-sm font-medium text-slate-700">{label}</label><div className="mt-1">{children}</div></div>;
